/**
 * Business Service - Frontend API calls for business-centric operations
 * Phase 3: Frontend adaptation for business-centric architecture
 */

import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com/api";

const isAuthError = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

/**
 * Get authentication headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem("ogestyle_token");
  if (!token) {
    window.location.href = "/login";
    return {};
  }
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Get all businesses owned by current user
 * @returns {Promise<Array>} Array of business objects
 */
export const getMyBusinesses = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/business/my-businesses`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data.businesses || [];
  } catch (error) {
    if (!isAuthError(error)) {
    }
    throw error;
  }
};

/**
 * Get locations for a specific business
 * @param {string} businessId - Business ID
 * @returns {Promise<Array>} Array of location objects
 */
export const getBusinessLocations = async (businessId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/business/${businessId}/locations`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data.locations || [];
  } catch (error) {
    if (!isAuthError(error)) {
    }
    throw error;
  }
};

/**
 * Get staff for a specific business
 * @param {string} businessId - Business ID
 * @returns {Promise<Object>} Staff data grouped by location
 */
export const getBusinessStaff = async (businessId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/staff/by-business/${businessId}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    if (!isAuthError(error)) {
    }
    throw error;
  }
};

/**
 * Get staff summary for a business
 * @param {string} businessId - Business ID
 * @returns {Promise<Object>} Staff summary statistics
 */
export const getBusinessStaffSummary = async (businessId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/staff/business-summary/${businessId}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    if (!isAuthError(error)) {
    }
    throw error;
  }
};

/**
 * Create a new location for a business
 * @param {Object} locationData - Location data including businessId
 * @returns {Promise<Object>} Created location
 */
export const createLocation = async (locationData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/shop/create`,
      locationData,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data.shop || response.data;
  } catch (error) {
    if (!isAuthError(error)) {
    }
    throw error;
  }
};

/**
 * Update location operating mode
 * @param {string} shopId - Location ID
 * @param {string} locationMode - commission_only | booth_rental_only | hybrid
 * @returns {Promise<Object>} Updated location mode payload
 */
export const updateLocationMode = async (shopId, locationMode) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/api/shop/${shopId}/mode`,
      { locationMode },
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    if (!isAuthError(error)) {
    }
    throw error;
  }
};

/**
 * Add staff to a location
 * @param {Object} staffData - Staff data including shopId, businessId
 * @returns {Promise<Object>} Created staff record
 */
export const addStaff = async (staffData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/staff/add`,
      staffData,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    if (!isAuthError(error)) {
    }
    throw error;
  }
};

/**
 * Get all locations (shops) for current user - v2 with business support
 * @returns {Promise<Array>} Array of shop/location objects
 */
export const getMyShopsV2 = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/shop/my-shops-v2`, {
      headers: getAuthHeaders(),
    });

    // Backend returns { ownedBusinesses: [{business, locations}], staffLocations: [] }
    // Flatten to a simple array of locations
    const result = response.data;
    const allLocations = [];

    // Add owned business locations
    if (result.ownedBusinesses) {
      result.ownedBusinesses.forEach((item) => {
        if (item.locations) {
          allLocations.push(...item.locations);
        }
      });
    }

    // Add staff-assigned locations
    if (result.staffLocations) {
      allLocations.push(...result.staffLocations);
    }

    return allLocations;
  } catch (error) {
    if (!isAuthError(error)) {
    }
    return []; // Return empty array on error instead of throwing
  }
};

/**
 * Get products for a specific shop
 * @param {string} shopId - Shop/Location ID
 * @returns {Promise<Array>} Array of products
 */
export const getShopProducts = async (shopId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/v2/products/shop/${shopId}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Get pending products for a shop (awaiting approval)
 * @param {string} shopId - Shop/Location ID
 * @returns {Promise<Array>} Array of pending products
 */
export const getPendingProducts = async (shopId) => {
  return [];
};

/**
 * Create a new product
 * @param {Object} productData - Product data
 * @returns {Promise<Object>} Created product
 */
export const createProduct = async (productData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/v2/products`,
      productData,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Approve or reject a product
 * @param {string} productId - Product ID
 * @param {boolean} approved - True to approve, false to reject
 * @param {string} rejectionReason - Reason for rejection (if rejected)
 * @returns {Promise<Object>} Updated product
 */
export const approveProduct = async (
  productId,
  approved,
  rejectionReason = "",
) => {
  throw new Error("Product approval workflow has been retired in v2.");
};

/**
 * Get staff list with optional business filter
 * @param {Object} params - Query parameters { businessId?, shopId? }
 * @returns {Promise<Array>} Array of staff members
 */
export const getStaffList = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await axios.get(
      `${API_BASE_URL}/api/staff/list?${queryString}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data || [];
  } catch (error) {
    throw error;
  }
};

export default {
  getMyBusinesses,
  getBusinessLocations,
  getBusinessStaff,
  getBusinessStaffSummary,
  createLocation,
  addStaff,
  getMyShopsV2,
  getShopProducts,
  getPendingProducts,
  createProduct,
  approveProduct,
  getStaffList,
};
